/**
 * Analytics event definitions — single source of truth for all tracked events.
 *
 * Rule: NEVER include financial values, PII, or form field contents.
 * Only track what action happened and minimal non-sensitive metadata.
 */

import { trackEvent } from './posthog';
import { parseStored, isStringRecord } from '../../utils/parseStored';

// ── Wizard events ─────────────────────────────────────────────────

export function trackWizardStepCompleted(step: number, planType?: string): void {
  trackEvent('wizard_step_completed', { step, ...(planType && { planType }) });
}

export function trackProjectionGenerated(strategy?: string): void {
  trackEvent('projection_generated', { ...(strategy && { strategy }) });
}

export function trackAlternativeCreated(): void {
  trackEvent('alternative_created');
}

export function trackComparisonViewed(): void {
  trackEvent('comparison_viewed');
}

// ── Auth events ───────────────────────────────────────────────────

export function trackRegisterStarted(): void {
  trackEvent('register_started');
}

export function trackRegisterCompleted(utmParams?: Record<string, string>): void {
  const safeParams: Record<string, string> = {};
  const allowed = new Set(UTM_KEYS as readonly string[]);
  if (utmParams) {
    for (const [key, val] of Object.entries(utmParams)) {
      if (allowed.has(key) && typeof val === 'string') {
        safeParams[key] = val.slice(0, 200);
      }
    }
  }
  trackEvent('register_completed', Object.keys(safeParams).length > 0 ? safeParams : undefined);
}

export function trackLogin(): void {
  trackEvent('login');
}

// ── Navigation events ─────────────────────────────────────────────

export function trackPricingViewed(): void {
  trackEvent('pricing_viewed');
}

export function trackToolOpened(slug: string): void {
  trackEvent('tool_opened', { slug });
}

// ── UTM helpers ───────────────────────────────────────────────────

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const UTM_STORAGE_KEY = 'gp_utm';

/**
 * Capture UTM params from the current URL and store in sessionStorage.
 * Call once on app load.
 */
export function captureUtmParams(): void {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) utm[key] = val;
  }
  if (Object.keys(utm).length > 0) {
    try { sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm)); } catch { /* noop */ }
  }
}

/**
 * Retrieve stored UTM params (for attaching to registration event).
 */
export function getStoredUtmParams(): Record<string, string> | undefined {
  try {
    return parseStored(sessionStorage.getItem(UTM_STORAGE_KEY), isStringRecord) ?? undefined;
  } catch {
    return undefined;
  }
}
